/**
 * Drone Data Model
 *
 * Represents a drone in the American Drone Network inventory system.
 * Used in the admin portal for drone assignment and management.
 *
 * @see american-drone-network-hub/src/data-models/drone-inventory.ts
 */

export interface DroneInventory {
  id: string;
  name: string;
  manufacturerId: string;
  modelId: string;
  serialNumber: string;
  packagingSerialNumber: string | null;
  version: string | null;
  status: DroneStatus;

  // Remote ID and broadcast module
  remoteId: string | null;
  remoteBroadcastModule: string | null;

  // FAA N-Number registration
  nNumber: string | null;
  nNumberActiveDate: Date | null;

  // Organization assignment (for Zoho Company linking)
  assignedOrganizationId: string | null;

  // Zoho FSM sync tracking (for Service Appointments/Assets)
  zohoFsmProductId: string | null;
  zohoSyncedAt: Date | null;
  zohoSyncStatus: 'pending' | 'synced' | 'error' | null;
  zohoSyncError: string | null;

  // Optional: populated when joined with manufacturer/model tables
  manufacturerName?: string;
  modelName?: string;

  // Audit fields
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  createdBy: string | null;
  updatedBy: string | null;
  deletedBy: string | null;
}

export type DroneStatus = 'available' | 'assigned' | 'maintenance' | 'retired';

/**
 * Drone Assignment
 *
 * Represents an assignment of a drone to an organization.
 */
export interface DroneAssignment {
  id: string;
  droneId: string;
  organizationId: string;
  assignedToProfileId: string | null;
  assignedByProfileId: string;
  assignedAt: Date;
  returnedAt: Date | null;
  returnedByProfileId: string | null;
  revokedAt: Date | null;
  revokedByProfileId: string | null;
  revocationReason: string | null;
  status: AssignmentStatus;
  notes: string | null;

  // Optional: populated from joins
  droneName?: string;
  droneSerialNumber?: string;
  organizationName?: string;
  assignedByName?: string;
  returnedByName?: string;

  // Audit fields
  createdAt: Date;
  updatedAt: Date;
}

export type AssignmentStatus = 'active' | 'returned' | 'revoked' | 'lost' | 'damaged';

/**
 * Drone source type - indicates whether the drone is ADN-leased or organization-owned
 */
export type DroneSource = 'adn' | 'organization';

/**
 * Organization status for org-owned drones
 */
export type OrgDroneStatus = 'active' | 'maintenance' | 'inactive' | 'retired';

/**
 * Unified Organization Drone View
 *
 * Represents a drone from the organization perspective.
 * Can be either an ADN-assigned drone or an organization-owned drone.
 * Used in the organization detail page Drones tab.
 */
export interface OrganizationDrone {
  id: string;
  serialNumber: string;
  name: string;
  nNumber: string | null;
  manufacturerName: string | null;
  modelName: string | null;
  status: string; // DroneStatus for ADN, OrgDroneStatus for org-owned
  source: DroneSource;

  // ADN-assigned drone fields
  assignmentId?: string;
  assignedAt?: Date;

  // Organization-owned drone fields
  createdAt?: Date;

  // Legacy fields for backwards compatibility
  lastUsed?: Date | null;
}

/**
 * API Response for organization drones endpoint
 * GET /admin/organizations/:id/drones
 */
export interface OrganizationDronesResponse {
  adnAssigned: OrganizationDrone[];
  organizationOwned: OrganizationDrone[];
}

/**
 * Get status badge class for Bootstrap styling
 */
export function getDroneStatusBadgeClass(status: DroneStatus): string {
  switch (status) {
    case 'available':
      return 'bg-success';
    case 'assigned':
      return 'bg-primary';
    case 'maintenance':
      return 'bg-warning';
    case 'retired':
      return 'bg-secondary';
    default:
      return 'bg-secondary';
  }
}

/**
 * Get status display label
 */
export function getDroneStatusLabel(status: DroneStatus): string {
  switch (status) {
    case 'available':
      return 'Available';
    case 'assigned':
      return 'Assigned';
    case 'maintenance':
      return 'Maintenance';
    case 'retired':
      return 'Retired';
    default:
      return status;
  }
}

/**
 * Get assignment status badge class
 */
export function getAssignmentStatusBadgeClass(status: AssignmentStatus): string {
  switch (status) {
    case 'active':
      return 'bg-success';
    case 'returned':
      return 'bg-secondary';
    case 'revoked':
      return 'bg-danger';
    case 'lost':
      return 'bg-danger';
    case 'damaged':
      return 'bg-warning';
    default:
      return 'bg-secondary';
  }
}

/**
 * Get assignment status label
 */
export function getAssignmentStatusLabel(status: AssignmentStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'returned':
      return 'Returned';
    case 'revoked':
      return 'Revoked';
    case 'lost':
      return 'Lost';
    case 'damaged':
      return 'Damaged';
    default:
      return status;
  }
}
